import debounce from 'lodash.debounce';
import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Autosuggest from 'react-autosuggest';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import Box from 'components/Box';
import { ClearButton } from 'components/Button';
import { Image } from 'components/Image';
import { LegacyInput } from 'components/LegacyInput';
import { TooltipStandalone } from 'components/TooltipStandalone';
import { REMOTE_KEYWORDS } from 'utils/constants/search';
import googleMaps from 'utils/googleMaps';
import * as localStorage from 'utils/localStorage';

import LocationIcon from 'location-pin.svg';
import RemoteIcon from 'remote.svg';

import { setGMapsError } from '../redux/main/actions/gMapsError';
import { colors, fontWeights, transition } from '../theme';
import Text from './Text';

const testMode = localStorage.getItem('testMode');
const defaultKeyword = REMOTE_KEYWORDS[USER_LOCALE][0];

const getCity = (addressComponents: AddressComponents) =>
  addressComponents.locality ||
  addressComponents.postal_town ||
  addressComponents.sublocality_level_1 ||
  addressComponents.administrative_area_level_3 ||
  addressComponents.administrative_area_level_2;

const StyledLocationIcon = styled(LocationIcon)`
  position: absolute;
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  left: 9px;
`;
const InputContainer = styled.div`
  display: inline-flex;
  position: relative;
  align-items: center;
  width: 100%;
`;
const AutosuggestWrapper = styled.div<{ borderSize: number }>`
  position: relative;

  .react-autosuggest {
    &__suggestions-container {
      display: none;
      padding: 0 ${({ borderSize }) => borderSize}px;

      &--open {
        display: block;
        margin-top: 4px;
        position: absolute;
        width: 100%;
        overflow-y: scroll;
        max-height: 400px;
        border: 1px solid;
        border-color: ${colors.silver};
        background-color: #fff;
        font-size: 18px;
        line-height: 19px;
        border-radius: 4px;
        z-index: 2;
      }
    }

    &__suggestions-list {
      margin: 0;
      padding: 0;
      list-style-type: none;
    }

    &__suggestion {
      cursor: pointer;
      padding: 6px 4px;
      transition: all ${transition};
      ${({ borderSize }) => `
        box-shadow: 0 -${borderSize}px rgba(255,255,255,0) inset, 0 ${borderSize}px rgba(255,255,255,0) inset;
      `}

      &:not(:first-child) {
        border-top: 1px dashed ${colors.aluminum};
      }

      &--highlighted {
        background-color: ${colors.offWhite};
        ${({ borderSize }) => `
          box-shadow: 0 -${borderSize}px white inset, 0 ${borderSize}px white inset;
        `}
      }
    }
  }
`;
// @ts-expect-error ts-migrate(2749) FIXME: 'InputContainer' refers to a value, but is being u... Remove this comment to see the full error message
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledInput = forwardRef<any, InputContainer>(
  (
    {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      showLocationIcon,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      onClear,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      className,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      gMapsErrored,
      ...inputProps
    }: {
      showLocationIcon: boolean;
      onClear: (() => void) | null | undefined;
      className?: string;
      gMapsErrored: boolean;
      'data-qa-id': string;
    },
    ref
  ) => (
    <InputContainer className={className}>
      {showLocationIcon && <StyledLocationIcon width="21" height="21" />}
      <LegacyInput
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputProps}
        ref={ref}
        height="50px"
        width="100%"
        pt={showLocationIcon ? '13px' : undefined}
        pb={showLocationIcon ? '14px' : undefined}
        pl={showLocationIcon ? '31px' : '18px'}
        pr={onClear ? '38px' : '18px'}
      />
      {onClear && (
        <ClearButton
          onClick={onClear}
          data-qa-id={`${inputProps['data-qa-id']}-clear`}
        />
      )}
      {gMapsErrored && (
        <TooltipStandalone>
          {getText(
            'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.'
          )}
        </TooltipStandalone>
      )}
    </InputContainer>
  )
);
type Props = {
  hasError?: boolean;
  onLocationChange: (
    arg0: SearchLocation,
    arg1: boolean | null | undefined,
    arg2: boolean | null | undefined
  ) => void;
  onLocationInputValueChange?: (
    arg0: InputEvent,
    arg1: {
      newValue: string;
    }
  ) => void;
  hasSuccess?: boolean;
  clearLocation: () => void;
  locationInputValue: string;
  'data-qa-id'?: string;
  placeholder: string | null | undefined;
  showLocationIcon?: boolean;
  showClearButton: boolean;
  className?: string;
  handlePlaceDetails?: (arg0: Place) => void;
  addressTypes?: Array<string>;
  includeCity?: boolean;
  includeFull?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur?: (...args: Array<any>) => any;
  disabled?: boolean;
  hasRemoteOptions?: boolean;
  highlightFirstSuggestion?: boolean;
  componentRestrictions?: Record<string, string | Array<string>>;
};

// eslint-disable-next-line react/no-multi-comp, react/function-component-definition
const LocationInput = ({
  hasError,
  hasSuccess,
  onLocationChange,
  onLocationInputValueChange,
  clearLocation,
  locationInputValue,
  placeholder,
  showLocationIcon = false,
  showClearButton,
  className,
  addressTypes,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion = true,
  componentRestrictions,
  hasRemoteOptions,
  onBlur,
  ...props
}: Props) => {
  const [autocompleteService, setAutocompleteService] = useState();
  const [geocoder, setGeocoder] = useState();
  const [sessionToken, setSessionToken] = useState();
  const dispatch = useDispatch();
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'gMapsError' does not exist on type 'Defa... Remove this comment to see the full error message
  const { gMapsErrored } = useSelector(({ gMapsError: { errored } }) => ({
    gMapsErrored: errored,
  }));

  const handlePlaceDetails = (place) => {
    const addressComponents = place.address_components.reduce(
      (hash, component) => {
        const attributeName = component.types[0];
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-param-reassign
        hash[attributeName] = [
          'country',
          'administrative_area_level_1',
        ].includes(attributeName)
          ? component.short_name
          : component.long_name;
        return hash;
      },
      {}
    );

    if (!('country' in addressComponents)) {
      // below: special handling of 'south east asia', 'gaza', etc.
      // i.e. results for which the autocomplete api does not return a country code
      // we will use the iso 3166 user-defined 'XA' code for this situation
      // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#User-assigned_code_elements
      addressComponents.country = 'XA';
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-param-reassign
      place.types[0] = 'undefined_country_geocodes';
    }

    const countryCode = addressComponents.country;
    const stateCode = addressComponents.administrative_area_level_1;
    let city;
    let full;

    if (includeCity) {
      city = getCity(addressComponents);
    }

    if (includeFull) {
      full = place.formatted_address;
    }

    const boundingBox = place.geometry.viewport.toJSON();
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 1.
    onLocationChange({
      text: place.formatted_address,
      stateCode,
      countryCode,
      boundingBoxN: boundingBox.north,
      boundingBoxW: boundingBox.west,
      boundingBoxS: boundingBox.south,
      boundingBoxE: boundingBox.east,
      geoType: place.types[0],
      latLongAddress: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      full,
      city,
    });
  };

  const onSuggestionSelected = (e, { suggestion }) => {
    if (!geocoder) return;

    if (suggestion.isRemoteOption) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line consistent-return
      return onLocationChange(
        {
          text: null,
          stateCode: null,
          countryCode: null,
          boundingBoxN: null,
          boundingBoxW: null,
          boundingBoxS: null,
          boundingBoxE: null,
          geoType: null,
          latitude: null,
          longitude: null,
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ text: null; stateCode: null; c... Remove this comment to see the full error message
          full: null,
          city: null,
        },
        true,
        true
      );
    }

    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line consistent-return
    return geocoder.geocode(
      {
        placeId: suggestion.place_id,
      },
      (results) =>
        props.handlePlaceDetails
          ? props.handlePlaceDetails(results[0])
          : handlePlaceDetails(results[0])
    );
  };

  const [suggestions, updateSuggestions] = useState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ReadonlyArray<Record<string, any>>
  >([]);
  const [inProgressQuery, updateInProgressQuery] = useState<
    | Promise<
        ReadonlyArray<{
          name: string;
          value: string;
        }>
      >
    | null
    | undefined
  >(null);
  useEffect(() => {
    if (!autocompleteService) {
      googleMaps()
        .then((maps) => {
          setAutocompleteService(new maps.places.AutocompleteService());
          setGeocoder(new maps.Geocoder());
          setSessionToken(new google.maps.places.AutocompleteSessionToken());
        })
        .catch((error) => {
          dispatch(setGMapsError(error.message));
        });
    }
  }, [autocompleteService, dispatch]);

  const getPlacePredictions = (input) =>
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line consistent-return
    new Promise((resolve, reject) => {
      if (!autocompleteService) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-promise-executor-return
        return reject();
      }

      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      autocompleteService.getPlacePredictions(
        {
          input,
          componentRestrictions,
          types: addressTypes || ['geocode'],
          sessionToken,
        },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(results);
          } else if (
            status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            resolve([]);
          } else {
            reject(new Error(status));
          }
        }
      );
    });

  const createRemotePrediction = (keyword: string) => ({
    description:
      keyword.toLowerCase() === defaultKeyword.toLowerCase()
        ? keyword
        : `${keyword} (${defaultKeyword})`,
    structured_formatting: {
      main_text:
        keyword.toLowerCase() === defaultKeyword.toLowerCase()
          ? `${keyword}`
          : `${keyword} (${defaultKeyword})`,
      secondary_text: '',
    },
    isRemoteOption: true,
  });

  const getRemoteOptions = (value: string) => {
    const option = REMOTE_KEYWORDS[USER_LOCALE].find((kw) =>
      kw.toLowerCase().startsWith(value.toLowerCase())
    );
    return option ? createRemotePrediction(option) : undefined;
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line consistent-return
  const loadSuggestions = (value: string) => {
    // Cancel the previous request
    if (inProgressQuery) {
      inProgressQuery.then(() => {
        debounce(() => loadSuggestions(value));
      });
    } else {
      const query = getPlacePredictions(value);
      const remoteOptions = [];

      if (hasRemoteOptions) {
        remoteOptions.push(getRemoteOptions(value));
      }

      query.then((options) => {
        // @ts-expect-error ts-migrate(2488) FIXME: Type 'unknown' must have a '[Symbol.iterator]()' m... Remove this comment to see the full error message
        updateSuggestions([...remoteOptions, ...options].filter(Boolean));
        updateInProgressQuery(null);
      });
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Promise<unknown>' is not assigna... Remove this comment to see the full error message
      updateInProgressQuery(query);
      return query;
    }
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    if (autocompleteService) {
      loadSuggestions(value);
    }
  };

  const renderSuggestion = useMemo(
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
    () => (suggestion) =>
      (
        <Box
          className="idlst-google-suggestion"
          display="flex"
          alignItems="center"
        >
          {/* TODO: Fix this the next time the file is edited. */}
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {suggestion.isRemoteOption ? (
            <Box ml="3px" mr="5px">
              <RemoteIcon width="18" height="18" />
            </Box>
          ) : (
            <Box mr="5px">
              <LocationIcon width="21" height="21" />
            </Box>
          )}
          <Box display="flex" alignItems="baseline" overflow="hidden">
            <Text
              styleAs="bodyMicro"
              fontWeight={fontWeights.semiBold}
              mr="5px"
              whiteSpace="nowrap"
            >
              {/* TODO: Fix this the next time the file is edited. */}
              {/* eslint-disable-next-line react/destructuring-assignment */}
              {suggestion.structured_formatting.main_text}
            </Text>
            <Text
              styleAs="bodyMicro"
              color={colors.middleGrey}
              whiteSpace="nowrap"
            >
              {/* TODO: Fix this the next time the file is edited. */}
              {/* eslint-disable-next-line react/destructuring-assignment */}
              {suggestion.structured_formatting.secondary_text}
            </Text>
          </Box>
        </Box>
      ),
    []
  );

  const onSuggestionsClearRequested = () => {
    updateSuggestions([]);
    updateInProgressQuery(null);
  };

  const renderSuggestionsContainer = ({
    containerProps,
    children,
  }: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    containerProps: Record<string, any>;
    children: ReactNode;
  }) => (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...containerProps}>
      {children}
      <Box display="flex" justifyContent="flex-end">
        <Box
          component={Image}
          width={150}
          layout="responsive"
          src="/static/images/poweredbygoogle/powered_by_google_on_white.png"
          srcSet="/static/images/poweredbygoogle/powered_by_google_on_white.png, /static/images/poweredbygoogle/powered_by_google_on_white@2x.png 2x, /static/images/poweredbygoogle/powered_by_google_on_white@3x.png 3x"
          alt="Powered by Google"
          alwaysShow
        />
      </Box>
    </div>
  );

  const onKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const getSuggestionValue = (suggestion) => suggestion.description;

  const inputProps = {
    className,
    disabled,
    hasSuccess,
    onChange: onLocationInputValueChange,
    onBlur,
    onKeyPress,
    onClear: showClearButton || testMode ? clearLocation : null,
    placeholder,
    gMapsErrored,
    showLocationIcon,
    value: locationInputValue || '',
    'aria-label': getText('Location'),
    'data-qa-id': props['data-qa-id'] || 'location-input',
  };
  return (
    <AutosuggestWrapper borderSize={4}>
      <Autosuggest
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={(v) => v && v.toString().trim().length >= 2}
        renderInputComponent={(renderInputComponentProps) => (
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/jsx-props-no-spreading
          <StyledInput {...renderInputComponentProps} />
        )}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={inputProps}
      />
    </AutosuggestWrapper>
  );
};

export default LocationInput;
